"use client";

import { useCallback, useState } from "react";
import { createClient } from "@/lib/supabase/client";
import { buildOAuthCallbackUrl } from "@/lib/auth-site";
import { cn } from "@/lib/utils";
import { authOAuthButton } from "@/lib/auth-ui";
import { useResetOnBfcache } from "@/lib/use-reset-on-bfcache";
import { setPendingAuth, type PendingAuthMode } from "@/lib/pending-auth";

function LinkedInMark({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width="20"
      height="20"
      aria-hidden
    >
      <path
        fill="#0A66C2"
        d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.36V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.38-1.85 3.61 0 4.28 2.38 4.28 5.47v6.27ZM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12Zm1.78 13.02H3.56V9h3.56v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0Z"
      />
    </svg>
  );
}

type LinkedInSignInButtonProps = {
  disabled?: boolean;
  label: string;
  buttonClassName?: string;
  /** Which modal to reopen if the user lands back on the site without
   * completing OAuth (back nav, provider error, etc.). Defaults to "login". */
  mode?: PendingAuthMode;
  /** Where to land the user after successful OAuth. Forwarded to the
   * /auth/callback handler so the homepage prompt-resume flow can run. */
  next?: string;
};

export function LinkedInSignInButton({
  disabled = false,
  label,
  buttonClassName,
  mode = "login",
  next,
}: LinkedInSignInButtonProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useResetOnBfcache(
    useCallback(() => {
      setBusy(false);
      setError(null);
    }, []),
  );

  async function handleClick() {
    setError(null);
    setBusy(true);

    const redirectTo = buildOAuthCallbackUrl(next);
    if (!redirectTo) {
      setError("Could not determine site URL. Set NEXT_PUBLIC_SITE_URL.");
      setBusy(false);
      return;
    }

    const supabase = createClient();
    // skipBrowserRedirect: true so setPendingAuth runs before navigation.
    const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "linkedin_oidc",
      options: { redirectTo, skipBrowserRedirect: true },
    });

    if (oauthError) {
      setError(oauthError.message);
      setBusy(false);
      return;
    }

    if (data.url) {
      setPendingAuth(mode);
      try {
        const back = new URL(window.location.href);
        back.searchParams.set(mode, "1");
        window.history.pushState({}, "", back.toString());
      } catch {
        // ignore — fallback is sessionStorage
      }
      console.log("[auth] redirecting to LinkedIn OAuth", { mode });
      window.location.assign(data.url);
      return;
    }

    setError("Could not start LinkedIn sign-in. Try again.");
    setBusy(false);
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        className={cn(authOAuthButton, "rounded-full", buttonClassName)}
        onClick={() => void handleClick()}
        disabled={disabled || busy}
      >
        <LinkedInMark />
        {busy ? "Redirecting…" : label}
      </button>
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
    </div>
  );
}
